/**
 * identity-service `/register` client (uid-001-3b).
 *
 * Hits `POST {VITE_IDENTITY_SERVICE_URL}/register` with the
 * `RegisterIdentityRequest` shape (verified against MERGED identity-service
 * `src/identity.rs`, contract in the story). The password is sent ONLY over
 * HTTPS in the request body and is NEVER logged, stored, or echoed.
 *
 * Register issues NO session/token — it returns `201 + Identity` only; the
 * session is minted at login (uid-001-5). So a 201 here means "account created,
 * now route back to origin / next step", not "authenticated".
 */

export interface RegisterRequest {
  email: string;
  password: string;
  /** `YYYY-MM-DD`. Validated + age-classified server-side, then discarded. */
  dob: string;
  /** Optional public profile name; omitted from the body when empty. */
  display_name?: string;
}

/**
 * Discriminated outcome of a register attempt. We classify by HTTP status into
 * the distinct UI branches the story requires (AC2). `fieldErrors` is parsed
 * from a 400 body when the server returns per-field detail; otherwise the UI
 * falls back to a generic inline message.
 */
export type RegisterResult =
  | { kind: 'success' }
  | { kind: 'under13' } // 403 — COPPA hard-block, no retry affordance
  | { kind: 'minor' } // 501 — 13-17, parental consent not built
  | { kind: 'conflict' } // 409 — already registered
  | { kind: 'validation'; message?: string } // 400
  | { kind: 'error' }; // 500 / network / unexpected

function identityServiceUrl(): string {
  const base = import.meta.env.VITE_IDENTITY_SERVICE_URL;
  if (!base) {
    // Surfaced as a generic error in the UI; the deploy build-arg wires this.
    throw new Error('VITE_IDENTITY_SERVICE_URL is not configured');
  }
  return base.replace(/\/$/, '');
}

export async function registerIdentity(
  req: RegisterRequest,
): Promise<RegisterResult> {
  // Build the body, omitting an empty display_name (the server field is
  // optional). The password is included in the body only — never logged.
  const body: RegisterRequest = {
    email: req.email,
    password: req.password,
    dob: req.dob,
  };
  if (req.display_name && req.display_name.trim()) {
    body.display_name = req.display_name.trim();
  }

  let res: Response;
  try {
    res = await fetch(`${identityServiceUrl()}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    // Network/CORS failure — generic, no detail (no PII, no internals).
    return { kind: 'error' };
  }

  switch (res.status) {
    case 201:
      return { kind: 'success' };
    case 403:
      return { kind: 'under13' };
    case 501:
      return { kind: 'minor' };
    case 409:
      return { kind: 'conflict' };
    case 400: {
      let message: string | undefined;
      try {
        const data = (await res.json()) as { error?: string; message?: string };
        message = data.error ?? data.message;
      } catch {
        message = undefined;
      }
      return { kind: 'validation', message };
    }
    default:
      return { kind: 'error' };
  }
}
